/**
 * @graphql cc
 */
import debounce from 'lodash/debounce';
import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { useExposeGoogleMeetScreenShare } from '@confluence/embedded-google-meet/entry-points/useExposeGoogleMeetScreenShare.ts';
import { ContentHeader } from '@confluence/content-header/entry-points/ContentHeader.ts';
import type { BaseViewPageProps } from '@confluence/view-page-common/entry-points/BaseViewPageProps.ts';
import { useViewPagePersistentScrollPosition } from '@confluence/view-page-common/entry-points/useViewPagePersistentScrollPosition.ts';
import { useViewPageParams } from '@confluence/view-page-common/entry-points/useViewPageParams.ts';
import { StartViewPageLoad } from '@confluence/view-page-common/entry-points/perf.config.ts';
import { ViewPageCommon } from '@confluence/view-page-common/entry-points/ViewPageCommon.ts';
import { useSpaceId } from '@confluence/space-utils/entry-points/useSpaceId.ts';
import { waitForGlobal } from '@confluence/wrm/entry-points/waitForGlobal.ts';
import { pagePublishedState } from '@confluence/editor-features/entry-points/pagePublishedState.ts';
import { getCachedPageData } from '@confluence/live-pages-utils/entry-points/getCachedPageData.ts';
import { useSearchSessionId } from '@confluence/search-session/entry-points/SearchSessionState.ts';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { expVal } from '@confluence/feature-experiments';

import { LegacyBridgeNext } from './LegacyBridgeNext.tsx';

export interface ViewPageProps extends BaseViewPageProps {
	ClassicEditorContextProviderLoader?: React.ComponentType<any>;
	children?: ReactNode;
}

const ViewPageInternal: FC<ViewPageProps> = ({
	contentId,
	spaceKey,
	// Pass ClassicEditorContextProviderLoader from parent classic package when ViewPageCommon may need to render LivePageComponent.
	// This is passed down from classic package instead of imported in LivePageComponent because we don't want to import from classic package when in next, it causes circular dependency.
	ClassicEditorContextProviderLoader,
	children,
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { versionOverride } = useViewPageParams();
	const [{ searchSessionId, additionalAnalytics }] = useSearchSessionId();

	useViewPagePersistentScrollPosition(contentId);

	// SSR-only A/A test for the Confluence FY27 H1 holdout. Fired only on the server (render body,
	// not a useEffect) to investigate whether SSR-based exposures cause the holdout's double
	// exposures: https://hello.atlassian.net/wiki/spaces/~6362282ab0b6ef03564afd8e/pages/7432450542
	// Exposure-only guard, renders no markup, so no hydration risk. #twc-holdout-working-group
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (process.env.REACT_SSR) {
		expVal('confluence_aa_test_fy27h1_ssr', 'isEnabled', false);
	}

	useEffect(() => {
		// A/A test for the Confluence FY26 H2 holdout. Contact #twc-holdout-working-group for questions.
		expVal('confluence_aa_test', 'isEnabled', false);
		// A/A test for the Confluence FY27 H1 holdout. Contact #twc-holdout-working-group for questions.
		expVal('confluence_aa_test_fy27h1', 'isEnabled', false);
	}, []);

	let contentHeader: React.ReactNode = null;
	if (contentId && spaceKey) {
		contentHeader = (
			<ContentHeader
				spaceKey={spaceKey}
				contentId={contentId}
				// @ts-ignore - Type 'number | null' is not assignable to type 'number | undefined'
				// This error was introduced after upgrading to TypeScript 5
				versionOverride={versionOverride}
			/>
		);
	}

	const spaceId = useSpaceId(spaceKey);

	useEffect(() => {
		// event is triggered by legacy JS when a new jira issue is created via JIM dialog
		waitForGlobal({ listener: 'ViewPage', globalProperty: 'AJS.bind' }, () => {
			window.AJS.bind('createGASv3Event', (_, data) => {
				createAnalyticsEvent(data).fire();
			});
		});

		if (pagePublishedState.pageJustPublished) {
			pagePublishedState.pageJustPublished = false;
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'pagePublished',
					source: 'viewPage',
					objectId: contentId,
					containerId: spaceId,
					containerType: 'space',
				},
			}).fire();
		}

		return () => {
			waitForGlobal({ listener: 'ViewPage', globalProperty: 'AJS.unbind' }, () => {
				window.AJS.bind('createGASv3Event');
			});
		};
	}, [createAnalyticsEvent, contentId, spaceId]);

	// Adds the `page scrolled (client)` instrumentation event to be used in the new metric definition for *engaged* users.
	useEffect(() => {
		const scrollContainer = document.getElementById('AkMainContent') ?? document;
		const handlePageScrolledAnalytics = () => {
			const { scrollX, scrollY, innerWidth, innerHeight } = window;
			const { scrollWidth, scrollHeight } = document.documentElement;

			// The user has reached the bottom of the page if `scrollY + innerHeight` equals `scrollHeight`.
			const attributes = {
				scrollX,
				scrollY,
				innerWidth,
				innerHeight,
				scrollWidth,
				scrollHeight,

				// The depths are rounded to two decimals; that's enough precision for scrolling.
				depthX: Math.trunc(((scrollX + innerWidth) / scrollWidth) * 100) / 100,
				depthY: Math.trunc(((scrollY + innerHeight) / scrollHeight) * 100) / 100,
				searchSessionId,
				...additionalAnalytics,
			};

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'scrolled',
					actionSubject: 'page',
					source: 'viewPage',
					objectId: contentId,
					containerId: spaceId,
					containerType: 'space',
					attributes,
				},
			})?.fire(); // used optional chaining so that scroll event can be tested - mocking fire causes infinite loop
		};

		// Once Safari supports the `scrollend` event we can remove the `debounce` logic on the `scroll` event and use `scrollend` intead.
		// https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollend_event
		const debouncePageScrolledAnalytics = debounce(handlePageScrolledAnalytics, 250 /*ms*/);

		scrollContainer.addEventListener('scroll', debouncePageScrolledAnalytics);
		return () => {
			scrollContainer.removeEventListener('scroll', debouncePageScrolledAnalytics);
			debouncePageScrolledAnalytics.cancel();
		};
	}, [createAnalyticsEvent, contentId, spaceId, searchSessionId, additionalAnalytics]);

	const cachedPageData = getCachedPageData(contentId);

	useExposeGoogleMeetScreenShare();

	return (
		<>
			<StartViewPageLoad contentId={contentId} />
			<ViewPageCommon
				spaceKey={spaceKey}
				contentId={contentId}
				contentHeader={contentHeader}
				hasPageComments
				hasLabels
				hasReactions
				hasInlineComments
				hasEOPRecs
				ClassicEditorContextProviderLoader={ClassicEditorContextProviderLoader}
				cachedPageData={cachedPageData}
			>
				{children}
				<LegacyBridgeNext />
			</ViewPageCommon>
		</>
	);
};

export const ViewPage: typeof ViewPageInternal = (props) => {
	return (
		<SuspenseWithDataFetcher dataFetcherName="content-main">
			<UFOSegment name="view-page">
				<ViewPageInternal {...props} />
			</UFOSegment>
		</SuspenseWithDataFetcher>
	);
};
